import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Form, FormField } from '../../../interfaces/form.interface';
import { DropdownData } from '../../../interfaces/input.interface';
import { ReactiveFormValueUtils } from '../../../model/reactive-form-value.utils';
import {
    AgriculturalFieldHelpDetails,
    AgriculturalHelpContext,
    buildAgriculturalFieldHelp,
    buildAgriculturalFieldHelpDetails,
} from '../../../utils/form-field-help.util';

@Component({
    selector: 'app-reactive-field-label',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './field-label.component.html',
    styleUrls: ['./field-label.component.scss'],
})
export class ReactiveFieldLabelComponent<T> implements OnChanges {
    @Input() field!: FormField<T>;

    @Input() form!: Form<T>;

    @Input() model!: FormGroup;

    @Input() originalModel: Record<string, unknown> = {};

    @Input() undoEnabled = false;

    @Input() tourAnchorId = '';

    @Input() sectionName = '';

    @Input() showFieldHelpButtons = false;

    @Output() undoRequested = new EventEmitter<FormField<T>>();

    public isHelpExpanded = false;

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['showFieldHelpButtons'] && !this.showFieldHelpButtons) {
            this.isHelpExpanded = false;
        }
    }



    public isFieldEdited(): boolean {
        const originalValue = this.originalModel[this.field.name ?? ''];
        const currentValue = this.model.get(this.field.name ?? '')?.value;

        return (this.field.conditions.hasUndoEnabled || this.undoEnabled)
            && !ReactiveFormValueUtils.areValuesEquivalent(
                currentValue,
                originalValue,
                ReactiveFormValueUtils.getFieldPrimaryKey(this.field),
            );
    }

    public sum(): string {
        if (this.form.type >= 3) {
            return '';
        }

        const summation = this.field.input?.dropdownConfiguration?.summation;

        if (!summation) {
            return '';
        }

        const selectedItems = this.resolveSelectedItemsForSummation();
        const total = selectedItems.reduce(
            (currentTotal, value) => currentTotal + parseFloat(String(value?.[summation.summand] ?? '0.00').replace(',', '.')),
            0
        );
        const formatter = summation.format;
        const totalFormatted = typeof formatter === 'function' ? formatter(total) : total.toFixed(2);

        return `(${totalFormatted} ${summation.uds})`;
    }

    private resolveSelectedItemsForSummation(): Array<Record<string, unknown>> {
        const dropdownData = this.field.input.data as DropdownData<T> | undefined;
        const controlValue = this.model.get(this.field.name ?? '')?.value;
        const selectedValues = Array.isArray(controlValue) ? controlValue : [controlValue];
        const primaryKey = ReactiveFormValueUtils.getFieldPrimaryKey(this.field);
        const identityKeys = ReactiveFormValueUtils.getFieldRawIdentityKeys(this.field);
        const options = ReactiveFormValueUtils.getDropdownOptions(dropdownData as DropdownData<unknown> | undefined);

        return selectedValues
            .map((value) => this.resolveSelectedItemForSummation(value, options, primaryKey, identityKeys))
            .filter((value): value is Record<string, unknown> => value !== null);
    }

    private resolveSelectedItemForSummation(
        value: unknown,
        options: ReturnType<typeof ReactiveFormValueUtils.getDropdownOptions>,
        primaryKey: string,
        identityKeys: readonly string[],
    ): Record<string, unknown> | null {
        if (!ReactiveFormValueUtils.hasNonEmptyValue(value)) {
            return null;
        }

        const comparableId = ReactiveFormValueUtils.extractComparableId(value, primaryKey, identityKeys);
        const option = comparableId === null
            ? null
            : options.find((candidate) => ReactiveFormValueUtils
                .getOptionComparableKeys(candidate, primaryKey, identityKeys)
                .includes(String(comparableId)));
        const rawValue = option?.raw ?? option?.value ?? value;

        return typeof rawValue === 'object' && rawValue !== null ? rawValue as Record<string, unknown> : null;
    }


    public getFieldHelpText(): string {
        return buildAgriculturalFieldHelp(this.field.name, this.field.label, this.getExplicitHelpText(), this.getHelpContext());
    }

    public getFieldHelpDetails(): AgriculturalFieldHelpDetails {
        return buildAgriculturalFieldHelpDetails(this.field.name, this.field.label, this.getExplicitHelpText(), this.getHelpContext());
    }

    public getHelpPanelId(): string {
        return `field-help-${this.field.name ?? 'unknown'}`;
    }

    public toggleFieldHelp(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isHelpExpanded = !this.isHelpExpanded;
    }

    public undo(): void {
        this.undoRequested.emit(this.field);
    }

    private getHelpContext(): AgriculturalHelpContext {
        return {
            ...(this.form?.name !== undefined ? { formName: this.form.name } : {}),
            ...(this.form?.header !== undefined ? { formHeader: this.form.header } : {}),
            ...(this.sectionName ? { sectionName: this.sectionName } : {}),
        };
    }

    private getExplicitHelpText(): string | undefined {
        const helpText = this.field['helpText'];

        return typeof helpText === 'string' ? helpText : undefined;
    }
}
